import { Icon } from '@iconify/react'
import { Badge, Box, Button, CircularProgress, Collapse, Divider, FormControl, Grid, IconButton, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select, Stack, TextField, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { TAnalyticsFilters } from '../../shared/types/analyticsFilters.type'
import { Delete } from '@mui/icons-material'
import { cloneDeep, isEmpty, omit, toNumber, uniqueId } from 'lodash'
import { t } from 'i18next'
import { LoadingButton } from '@mui/lab'
import { TinsightValueCritearia } from '../../shared/types/analyticView.type'
import { renderUnit } from '../../shared/utils/myTrainings'
import { useUserActivityLog } from '../../hooks/useUserActivityLog'

export type FilterParamsValue = Omit<SelectedInsightValue, 'unit'>

type MoreFiltersProps = {
    filters?: TAnalyticsFilters,
    loading: boolean,
    handleSave: (values: FilterParamsValue[]) => void,
    defaultViewCritearia?: TinsightValueCritearia[],
    openNewParamsModal: () => void
}


export type AvailableOperators = '>' | '<' | '<=' | '>=' | '='

interface SelectedInsightValue {
    field: string | null,
    operator: AvailableOperators | null,
    value: number | null,
    unit: string | null
}


const DEFAULT_CRITEARIA = 'insight.time_best_600'

function MoreFilters({ openNewParamsModal, filters, loading, handleSave, defaultViewCritearia }: MoreFiltersProps) {
    const [open, setOpen] = useState(false)

    const [selectedParams, setSelectedParams] = useState<SelectedInsightValue[]>([])

    const initialInsightFilterValues = filters?.insight_value_filters ?? []

    const analyticsEl = document.getElementById("analytics_content");
    const isFullscreen = document.fullscreenElement === analyticsEl;

        const { track } = useUserActivityLog()
    
    useEffect(() => {
        if (defaultViewCritearia && !isEmpty(defaultViewCritearia)) {
            setSelectedParams(defaultViewCritearia.map(i => ({
                value: i.value,
                operator: i.operator,
                field: i.insight_parameter.name,
                unit: i.insight_parameter.unit_translated
            })))
        } else {
            setSelectedParams([])
        }

    }, [defaultViewCritearia])


    useEffect(() => {
        onSave()
    }, [selectedParams.length])


    const addNewParam = () => {

        setSelectedParams(prev => [...prev, {
            field: null,
            operator: null,
            value: null,
            unit: ''
        }])

    }

    const onSave = () => {

        let data: FilterParamsValue[] = []
        if (isEmpty(selectedParams)) {
            handleSave(data)
            return
        }


        //We dont send param if any of operator value is null
        let mappedValues = selectedParams.filter(param => !Object.values(param).some(v => !v))

        if (!isEmpty(mappedValues)) {
            mappedValues.forEach(value => data.push(omit(value, 'unit')))
        }


        handleSave(data)
        track('ANALYTICS_INSIGHT_CRITERIA_ADDED')
    }

    const onDelete = (index: number) => {
        setSelectedParams(prev => {
            let items = [...prev]
            items.splice(index, 1)
            return items
        })
    }

    const updateWithSelectedParam = (value: string | null, index: number) => {
        if (value) {
            let items = [...selectedParams]
            let param = initialInsightFilterValues.find(i => i.insight_parameter.name === value)

            if (!param) return

            //Default Operator
            const operator: AvailableOperators = '<='

            let itemToUpdate = items[index]

            itemToUpdate.field = param.insight_parameter.name
            itemToUpdate.value = param.max_value
            itemToUpdate.operator = operator
            itemToUpdate.unit = param.insight_parameter.unit_translated

            items[index] = itemToUpdate


            setSelectedParams(items)


        }
    }


    const updateItem = (key: keyof SelectedInsightValue, value: number | string, index: number) => {

        setSelectedParams(prev => prev.map((item, i) => {
            return index !== i ? item : {
                ...item,
                [key]: value
            }
        }))

    }

    const getOperatorTitle = (operator: AvailableOperators) => {
        switch (operator) {
            case '>':
                return t('operator:superior')
            case '<':
                return t('operator:lower')

            case '<=':
                return t('operator:inferiorOrEqual')

            case '>=':
                return t('operator:superiorOrEqual')

            case '=':
                return t('operator:equal')


            default:
                break;
        }
    }

    return (
        <Stack direction={'column'} spacing={2} width={'100%'}>
            <Box display={'flex'} justifyContent={'end'} alignItems={'center'} gap={2}>
                <Badge
                    invisible={isEmpty(selectedParams)}
                    color="primary" variant="dot"
                    anchorOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                    }}
                    overlap="circular" // or "rectangular" depending on the button shape
                    sx={{ "& .MuiBadge-badge": { right: 2, top: 0 } }}

                >
                    <Button
                        disabled={loading}
                        variant="outlined"
                        onClick={() => setOpen(!open)}
                        color="primary"
                        endIcon={<Icon icon={!open ? 'prime:chevron-down' : 'prime:chevron-up'} />}
                    >
                        {t('analytics:moreFilters')}
                    </Button>
                </Badge>
                <Button variant='outlined' onClick={openNewParamsModal} startIcon={<Icon icon={'si:dashboard-customize-duotone'}></Icon>}>{t('analytics:customize')}</Button>

            </Box>
            <Collapse in={open}>
                <Stack direction={'column'} spacing={2}>
                    <Box display={'flex'} justifyContent={'space-between'} flexWrap={'wrap'} alignItems={'center'} gap={1}>
                        <Typography variant='h6'>{t('analytics:filterByValueTitle')}</Typography>
                        <Button disabled={loading} variant='outlined' onClick={addNewParam} startIcon={<Icon icon={'garden:filter-stroke-16'}></Icon>}>{t('analytics:addCriteria')}</Button>
                    </Box>

                    {
                        !isEmpty(selectedParams) && <Divider />
                    }

                    {selectedParams.map((param, index) => (
                        <Grid container spacing={2} key={param.field ?? uniqueId('param-')} >
                            <Grid item md={4} xs={12}>
                                <FormControl fullWidth >
                                    <InputLabel id="demo-simple-select-label">{t('settings:selectParameter')}</InputLabel>
                                    <Select
                                        MenuProps={{
                                            container: isFullscreen ? analyticsEl : undefined
                                        }}
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        value={param.field}
                                        label={t('settings:selectParameter')}
                                        onChange={(e) => { updateWithSelectedParam(e.target.value, index) }}
                                    >
                                        {
                                            initialInsightFilterValues.map(insight => (
                                                <MenuItem value={insight.insight_parameter.name}>{insight.insight_parameter.name_translated}</MenuItem>
                                            ))
                                        }

                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={4} xs={12}>
                                <FormControl fullWidth >
                                    <InputLabel id="demo-simple-select-label">{t('analytics:operator')}</InputLabel>
                                    <Select
                                        labelId="demo-simple-select-label"
                                        id="demo-simple-select"
                                        MenuProps={{
                                            container: isFullscreen ? analyticsEl : undefined
                                        }}
                                        value={param.operator}
                                        label={t('analytics:operator')}
                                        onChange={(e) => { updateItem('operator', e.target.value as AvailableOperators, index) }}
                                    >
                                        {
                                            ['>', '<', '<=', '>=', '='].map(op => (
                                                <MenuItem value={op}>{getOperatorTitle(op as AvailableOperators)}</MenuItem>
                                            ))
                                        }

                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item md={3} xs={10}>
                                <FormControl fullWidth>
                                    <InputLabel htmlFor="outlined-adornment-password">{t('raceDetails:value')}</InputLabel>

                                    <OutlinedInput type='number'
                                        onChange={(e) => { updateItem('value', toNumber(e.target.value), index) }}
                                        defaultValue={param.value}
                                        fullWidth
                                        label={t('raceDetails:value')}
                                        endAdornment={<InputAdornment position="end">{renderUnit(param?.unit ?? "")}</InputAdornment>}
                                    ></OutlinedInput>
                                </FormControl>
                            </Grid>

                            <Grid item md={1} xs={2} display={'flex'} justifyContent={'center'} alignItems={'center'}>

                                <IconButton color='error' onClick={() => onDelete(index)}>
                                    <Delete></Delete>
                                </IconButton>
                            </Grid>
                        </Grid>
                    ))}


                    {
                        !isEmpty(selectedParams) &&
                        <Box display={'flex'} justifyContent={'end'}>
                            <LoadingButton loading={loading} variant='contained' onClick={onSave}>{t('daterangepicker:applyLabel')}</LoadingButton>
                        </Box>
                    }
                </Stack>

            </Collapse >


        </Stack >
    )
}

export default MoreFilters